import { useEffect, useMemo, useState } from "react";

/**
 * Returns whether the user's daily reminder/alarm time has already passed
 * *today* (in the device's local wall-clock).
 *
 * This is the single trigger that flips a still-`unconfirmed` office day from
 * the purple "Planned" variant to the red "Unconfirmed" variant. The rule is
 * uniform across future / today / past:
 *   • future day            → alarm not yet reached  → Planned
 *   • today, before alarm    → alarm not yet reached  → Planned
 *   • today, at/after alarm   → alarm fired           → Unconfirmed
 *   • past day               → alarm long fired       → Unconfirmed
 *
 * A lightweight 60s interval re-evaluates the boolean so the calendar flips
 * within a minute of the alarm without a manual refresh.
 *
 * @param reminderTime 24h "HH:MM" string (defaults to 09:00 when unset/invalid)
 */
export function useTodayReminderPassed(reminderTime?: string | null): boolean {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  return useMemo(() => {
    const raw = reminderTime && /^\d{1,2}:\d{2}/.test(reminderTime)
      ? reminderTime
      : "09:00";
    const [h, m] = raw.split(":").map((n) => parseInt(n, 10));
    const targetMinutes = (Number.isFinite(h) ? h : 9) * 60 + (Number.isFinite(m) ? m : 0);

    const now = new Date(nowMs);
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    return nowMinutes >= targetMinutes;
  }, [nowMs, reminderTime]);
}
